/**
 * FarmRoadDashboard
 *
 * Tableau de bord pour visualiser les données d'exploitation
 * récupérées depuis la plateforme FarmRoad via son API publique.
 *
 * Fonctionnalités :
 *   - Liste des fermes accessibles avec sélection
 *   - Détail de la ferme : Compartments → Sectors
 *   - Boutons de plages rapides : Aujourd'hui / 7j / 30j / 3m / 1 an
 *   - Récupération parallèle avec barre de progression
 *   - Table paginée (200 lignes/page) avec tri et filtrage
 *   - Export CSV des données récupérées
 *   - Annulation en cours de récupération
 *
 * OPTIMISATIONS :
 *   - Chargement de la structure de la ferme (compartiments, secteurs)
 *     depuis PostgreSQL au lieu de l'API FarmRoad pour un affichage ultra-rapide
 *   - Synchronisation automatique si les données ne sont pas en cache
 *   - Les mesures utilisent également le cache PostgreSQL (mode Charger)
 *     ou peuvent être rafraîchies depuis FarmRoad (mode Rafraîchir)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  CircularProgress,
  Alert,
  Button,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Server,
  Activity,
  RefreshCw,
  ChevronRight,
  AlertCircle,
  Calendar,
  Download,
  X,
  ChevronLeft,
  Key,
  Sparkles,
  BarChart3,
  Settings,
  Map,
  History,
  Zap,
  Cloud,
  Menu
} from 'lucide-react';

import FarmRoadService from './FarmRoadService';
import FarmRoadInsightsPanel from './FarmRoadInsightsPanel';
import FarmRoadHistoryPanel from './FarmRoadHistoryPanel';
import FarmRoadSettingsPanel from './FarmRoadSettingsPanel';
import FarmRoadAutomationPanel from './FarmRoadAutomationPanel';
import FarmRoadWeatherPanel from './FarmRoadWeatherPanel';
import FarmMapView from './FarmMapView';
import { auth } from '../firebase';

// ===========================================================================
// Constantes
// ===========================================================================
const PAGE_SIZE = 200; // lignes par page dans la table

// Plages rapides : [label, fn() → { startDate, endDate }]
const QUICK_RANGES = [
  {
    label: "Aujourd'hui",
    compute: () => {
      const now = new Date();
      const start = new Date(now); start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }
  },
  {
    label: '7 jours',
    compute: () => {
      const now = new Date();
      const start = new Date(now); start.setDate(start.getDate() - 7);
      return { start, end: now };
    }
  },
  {
    label: '30 jours',
    compute: () => {
      const now = new Date();
      const start = new Date(now); start.setDate(start.getDate() - 30);
      return { start, end: now };
    }
  },
  {
    label: '3 mois',
    compute: () => {
      const now = new Date();
      const start = new Date(now); start.setMonth(start.getMonth() - 3);
      return { start, end: now };
    }
  },
  {
    label: '1 an',
    compute: () => {
      const now = new Date();
      const start = new Date(now); start.setFullYear(start.getFullYear() - 1);
      return { start, end: now };
    }
  }
];

// ===========================================================================
// Utilitaire : formater une date pour un <input type="datetime-local">
// ===========================================================================
const toLocalDatetimeString = (date) => date.toISOString().slice(0, 16);

// ===========================================================================
// Utilitaire : Export CSV
// ===========================================================================
const exportCSV = (measurements, farmId = null, farmDetail = null) => {
  if (!measurements || measurements.length === 0) return;

  // Fonction pour charger les mappings si nécessaire
  const loadMappings = async () => {
    const compartmentMap = {};
    const sectorMap = {};

    // Essayer de charger depuis la base PostgreSQL si farmId est fourni
    if (farmId) {
      try {
        // Charger les compartiments
        const compartmentsData = await FarmRoadService.getCompartments(farmId);
        if (Array.isArray(compartmentsData)) {
          compartmentsData.forEach(comp => {
            const compartmentFarmRoadId = comp.compartmentId || comp.compartment_id || comp.id;
            if (compartmentFarmRoadId) {
              compartmentMap[compartmentFarmRoadId] = comp.name || compartmentFarmRoadId;
            }
          });
        }

        // Charger les secteurs
        const sectorsData = await FarmRoadService.getSectors(farmId);
        if (Array.isArray(sectorsData)) {
          sectorsData.forEach(sector => {
            const sectorFarmRoadId = sector.sectorId || sector.sector_id || sector.id;
            if (sectorFarmRoadId) {
              sectorMap[sectorFarmRoadId] = sector.name || sectorFarmRoadId;
            }
          });
        }
      } catch (error) {
        console.warn('Erreur lors du chargement des mappings pour l\'export CSV:', error);
        // Utiliser farmDetail comme fallback
        if (farmDetail?.compartments) {
          farmDetail.compartments.forEach(comp => {
            const compartmentFarmRoadId = comp.compartmentId || comp.compartment_id || comp.id;
            if (compartmentFarmRoadId) {
              compartmentMap[compartmentFarmRoadId] = comp.name || compartmentFarmRoadId;
            }
            comp.sectors?.forEach(sector => {
              const sectorFarmRoadId = sector.sectorId || sector.sector_id || sector.id;
              if (sectorFarmRoadId) {
                sectorMap[sectorFarmRoadId] = sector.name || sectorFarmRoadId;
              }
            });
          });
        }
      }
    } else if (farmDetail?.compartments) {
      // Utiliser seulement farmDetail si pas de farmId
      farmDetail.compartments.forEach(comp => {
        const compartmentFarmRoadId = comp.compartmentId || comp.compartment_id || comp.id;
        if (compartmentFarmRoadId) {
          compartmentMap[compartmentFarmRoadId] = comp.name || compartmentFarmRoadId;
        }
        comp.sectors?.forEach(sector => {
          const sectorFarmRoadId = sector.sectorId || sector.sector_id || sector.id;
          if (sectorFarmRoadId) {
            sectorMap[sectorFarmRoadId] = sector.name || sectorFarmRoadId;
          }
        });
      });
    }

    return { compartmentMap, sectorMap };
  };

  // Fonction asynchrone pour générer le CSV
  const generateCSV = async () => {
    const { compartmentMap, sectorMap } = await loadMappings();

    const headers = ['Horodatage', 'Type', 'Source', 'Secteur', 'Valeur', 'Unité'];
    const rows = measurements.map((m) => {
      const timestamp = m.timestamp ? new Date(m.timestamp).toLocaleString('fr-FR') : '';
      const type      = m.type || '';
      const source    = m.sourceId || m.source?.id || '';
      
      // Utiliser les mappings pour formater le secteur
      const compartmentName = m.compartmentId ? compartmentMap[m.compartmentId] : null;
      const sectorName = m.sectorId ? sectorMap[m.sectorId] : null;

      // Overrides spécifiques (ex: Ferme de Larache)
      const compIdStr = m.compartmentId != null ? String(m.compartmentId) : '';
      const secIdStr = m.sectorId != null ? String(m.sectorId) : '';
      const specialOverride = (compIdStr === '4082' && secIdStr === '6806')
        ? 'S03'
        : (compIdStr === '4082' && secIdStr === '6810')
        ? 'S07'
        : null;
      
      const secteur = specialOverride || (
        m.compartmentId && m.sectorId
          ? `${compartmentName || m.compartmentId} / ${sectorName || m.sectorId}`
          : compartmentName || sectorName || m.compartmentId || m.sectorId || ''
      );
      
      const value     = m.value != null ? m.value : (m.readings?.[0]?.value != null ? m.readings[0].value : '');
      const unit      = m.unit || m.readings?.[0]?.unit || '';
      
      // Échapper les virgules dans les champs
      return [timestamp, type, source, secteur, value, unit].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM pour Excel
    const url  = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `farmroad_mesures_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Lancer la génération du CSV
  generateCSV().catch(error => {
    console.error('Erreur lors de la génération du CSV:', error);
    // Fallback: exporter sans les mappings
    const headers = ['Horodatage', 'Type', 'Source', 'Secteur', 'Valeur', 'Unité'];
    const rows = measurements.map((m) => {
      const timestamp = m.timestamp ? new Date(m.timestamp).toLocaleString('fr-FR') : '';
      const type      = m.type || '';
      const source    = m.sourceId || m.source?.id || '';
      const secteur   = m.compartmentId && m.sectorId
        ? `${m.compartmentId} / ${m.sectorId}`
        : m.compartmentId || m.sectorId || '';
      const value     = m.value != null ? m.value : (m.readings?.[0]?.value != null ? m.readings[0].value : '');
      const unit      = m.unit || m.readings?.[0]?.unit || '';
      return [timestamp, type, source, secteur, value, unit].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `farmroad_mesures_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  });
};

// ===========================================================================
// Sous-composant : sélecteur de ferme (dropdown)
// ===========================================================================
const FarmSelector = ({ farms, selectedFarm, onChange, loading }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      <Server className="h-4 w-4 inline mr-2 text-blue-500" />
      Sélectionner une ferme
    </label>
    <select
      value={selectedFarm?.id || ''}
      onChange={(e) => {
        const farm = farms.find(f => String(f.id) === e.target.value);
        if (farm) onChange(farm);
      }}
      disabled={loading}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:border-blue-400 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
    >
      <option value="">-- Choisissez une ferme --</option>
      {farms.map(farm => {
        const location = farm.location?.city || farm.location?.region || farm.location?.country || '';
        return (
          <option key={farm.id} value={farm.id}>
            {farm.name || farm.id} {location ? `(${location})` : ''}
          </option>
        );
      })}
    </select>
  </div>
);

// ===========================================================================
// Sous-composant : table paginée des mesures
// ===========================================================================
const MeasurementsTable = ({ measurements, filterType, farmDetail, farmId }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [compartments, setCompartments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Filtrer par type si un filtre est actif (correspondance exacte)
  const filtered = filterType
    ? measurements.filter((m) => (m.type || '').toLowerCase() === filterType.toLowerCase())
    : measurements;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Remettre à la page 0 si le filtre change
  const safePage   = Math.min(currentPage, totalPages - 1);
  const pageData   = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Charger les compartiments et secteurs depuis la base PostgreSQL
  useEffect(() => {
    const loadMappings = async () => {
      if (!farmId) return;
      
      setLoadingMappings(true);
      try {
        // Charger les compartiments
        const compartmentsData = await FarmRoadService.getCompartments(farmId);
        setCompartments(Array.isArray(compartmentsData) ? compartmentsData : []);

        // Charger tous les secteurs
        const sectorsData = await FarmRoadService.getSectors(farmId);
        setSectors(Array.isArray(sectorsData) ? sectorsData : []);
      } catch (error) {
        console.warn('Erreur lors du chargement des mappings:', error);
        // Utiliser les données de farmDetail comme fallback
        if (farmDetail?.compartments) {
          const allSectors = [];
          farmDetail.compartments.forEach(comp => {
            if (comp.sectors) {
              allSectors.push(...comp.sectors);
            }
          });
          setSectors(allSectors);
        }
      } finally {
        setLoadingMappings(false);
      }
    };

    loadMappings();
  }, [farmId, farmDetail]);

  // Créer des mappings pour enrichir les sources et secteurs
  const deviceMap = {};
  const compartmentMap = {};
  const sectorMap = {};

  // Mapping historique pour les anciens secteurs (avant migration)
  const historicalSectorMapping = {
    '6806': 'S3',
    '6810': 'S7'
  };

  // Utiliser d'abord les données de farmDetail (si disponibles)
  if (farmDetail?.compartments) {
    farmDetail.compartments.forEach(comp => {
      // Mapper les compartiments
      const compartmentFarmRoadId = comp.compartmentId || comp.compartment_id || comp.id;
      if (compartmentFarmRoadId) {
        compartmentMap[compartmentFarmRoadId] = comp.name || compartmentFarmRoadId;
      }

      comp.sectors?.forEach(sector => {
        // Mapper les secteurs
        const sectorFarmRoadId = sector.sectorId || sector.sector_id || sector.id;
        if (sectorFarmRoadId) {
          sectorMap[sectorFarmRoadId] = sector.name || sectorFarmRoadId;
        }

        sector.devices?.forEach(device => {
          deviceMap[device.id] = device.name || device.id;
        });
      });
    });
  }

  // Enrichir avec les données de la base PostgreSQL (priorité)
  compartments.forEach(comp => {
    const compartmentFarmRoadId = comp.compartmentId || comp.compartment_id || comp.id;
    if (compartmentFarmRoadId) {
      compartmentMap[compartmentFarmRoadId] = comp.name || compartmentFarmRoadId;
    }
  });

  sectors.forEach(sector => {
    const sectorFarmRoadId = sector.sectorId || sector.sector_id || sector.id;
    if (sectorFarmRoadId) {
      sectorMap[sectorFarmRoadId] = sector.name || sectorFarmRoadId;
    }
  });

  // Ajouter les secteurs historiques au mapping
  Object.entries(historicalSectorMapping).forEach(([oldId, name]) => {
    if (!sectorMap[oldId]) {
      sectorMap[oldId] = name;
    }
  });

  // Reset page quand le filtre change
  useEffect(() => { setCurrentPage(0); }, [filterType]);

  if (filtered.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic text-center py-6">
        Aucune mesure trouvée pour les filtres sélectionnés.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Info synthétique */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{filtered.length.toLocaleString('fr-FR')} mesure{filtered.length !== 1 ? 's' : ''} au total</span>
        <span>Page {safePage + 1} / {totalPages}</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600 uppercase text-xs">
              <th className="text-left px-3 sm:px-4 py-3 font-semibold">Horodatage</th>
              <th className="text-left px-3 sm:px-4 py-3 font-semibold">Type</th>
              <th className="text-left px-3 sm:px-4 py-3 font-semibold hidden md:table-cell">Source</th>
              <th className="text-left px-3 sm:px-4 py-3 font-semibold hidden sm:table-cell">Secteur</th>
              <th className="text-right px-3 sm:px-4 py-3 font-semibold">Valeur</th>
              <th className="text-left px-3 sm:px-4 py-3 font-semibold">Unité</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageData.map((m, index) => {
              // Enrichir la source avec le nom du device si disponible
              const sourceId = m.sourceId || m.source?.id;
              const sourceName = sourceId ? deviceMap[sourceId] : null;
              const sourceDisplay = sourceName
                ? `${sourceName} (${sourceId})`
                : sourceId
                ? `${sourceId}`
                : '—';

              // Afficher le secteur — priorité au nom du secteur seul
              const compartmentName = m.compartmentId ? compartmentMap[m.compartmentId] : null;
              const sectorName = m.sectorId ? sectorMap[m.sectorId] : null;

              // Utiliser les noms mappés depuis la base de données (plus de hardcoded mapping)
              const secteurDisplay = m.compartmentId && m.sectorId
                ? `${compartmentName || m.compartmentId} / ${sectorName || m.sectorId}`
                : compartmentName || sectorName || m.compartmentId || m.sectorId || '—';

              return (
                <tr key={index} className="bg-white hover:bg-gray-50 transition-colors">
                  <td className="px-3 sm:px-4 py-2 text-gray-600 whitespace-nowrap text-xs sm:text-sm">
                    {m.timestamp ? new Date(m.timestamp).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td className="px-3 sm:px-4 py-2">
                    <Chip label={m.type || '—'} size="small" variant="outlined" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }} />
                  </td>
                  <td className="px-3 sm:px-4 py-2 text-gray-600 hidden md:table-cell">{sourceDisplay}</td>
                  <td className="px-3 sm:px-4 py-2 text-gray-500 text-xs hidden sm:table-cell">{secteurDisplay}</td>
                  <td className="px-3 sm:px-4 py-2 text-right font-semibold text-gray-800">
                    {m.value != null ? m.value : (m.readings?.[0]?.value != null ? m.readings[0].value : '—')}
                  </td>
                  <td className="px-3 sm:px-4 py-2 text-gray-500 text-xs sm:text-sm">{m.unit || m.readings?.[0]?.unit || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Navigation de pages */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={safePage === 0}
            onClick={() => setCurrentPage(safePage - 1)}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>

          {/* Pages voisines */}
          {(() => {
            const pages = [];
            const start = Math.max(0, safePage - 2);
            const end   = Math.min(totalPages - 1, safePage + 2);
            if (start > 0)  pages.push({ type: 'num', value: 0 }, { type: 'dots' });
            for (let i = start; i <= end; i++) pages.push({ type: 'num', value: i });
            if (end < totalPages - 1) pages.push({ type: 'dots' }, { type: 'num', value: totalPages - 1 });
            return pages.map((p, idx) =>
              p.type === 'dots'
                ? <span key={`dots-${idx}`} className="text-gray-400 text-sm">...</span>
                : (
                  <button
                    key={p.value}
                    onClick={() => setCurrentPage(p.value)}
                    className={[
                      'w-8 h-8 rounded text-sm',
                      p.value === safePage ? 'bg-blue-600 text-white font-semibold' : 'text-gray-600 hover:bg-gray-100'
                    ].join(' ')}
                  >
                    {p.value + 1}
                  </button>
                )
            );
          })()}

          <button
            disabled={safePage >= totalPages - 1}
            onClick={() => setCurrentPage(safePage + 1)}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// Sous-composant : Modal de configuration de clé API
// ===========================================================================
const ApiKeyConfigModal = ({ open, onClose, onSave, loading, error: modalError }) => {
  const [apiKey, setApiKey] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSave = () => {
    if (!apiKey.trim()) {
      setLocalError('Veuillez entrer une clé API');
      return;
    }
    setLocalError('');
    onSave(apiKey.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Key className="h-5 w-5 text-blue-600" />
          <Typography variant="h6">Configuration FarmRoad</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Pour accéder à vos données FarmRoad, veuillez entrer votre clé API personnelle.
            Vous pouvez obtenir cette clé depuis votre compte FarmRoad.
          </Typography>
          <TextField
            autoFocus
            label="Clé API FarmRoad"
            type="text"
            fullWidth
            variant="outlined"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={loading}
            error={Boolean(localError || modalError)}
            helperText={localError || modalError}
            placeholder="Entrez votre clé API..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Validation...' : 'Valider'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ===========================================================================
// Composant principal : FarmRoadDashboard
// ===========================================================================
const FarmRoadDashboard = () => {
  // -------------------------------------------------------------------------
  // Responsive
  // -------------------------------------------------------------------------
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // -------------------------------------------------------------------------
  // État
  // -------------------------------------------------------------------------
  // Configuration clé API
  const [hasApiKey, setHasApiKey] = useState(null); // null = checking, true = has key, false = needs key
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');

  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [farmDetail, setFarmDetail] = useState(null);
  const [measurements, setMeasurements] = useState([]);

  const [loading, setLoading] = useState({ farms: true, detail: false, measurements: false });
  const [error, setError] = useState(null);

  // État pour les onglets du tableau de bord
  const [currentTab, setCurrentTab] = useState(0); // 0 = Météo, 1 = Mesures, 2 = Analyse IA, 3 = Historique, 4 = Paramètres, 5 = Automatisation
  const [drawerOpen, setDrawerOpen] = useState(false); // Toggle menu drawer sur mobile

  // État pour la carte interactive
  const [showMap, setShowMap] = useState(false); // Toggle affichage de la carte
  const [selectedSensor, setSelectedSensor] = useState(null); // Capteur sélectionné sur la carte
  const [selectedSectorId, setSelectedSectorId] = useState(null); // Secteur sélectionné sur la carte

  // État pour l'utilisateur (nécessaire pour l'analyse IA)
  const [userId, setUserId] = useState(null);

  // État pour l'analyse IA (persiste lors du changement d'onglet)
  const [analysisResult, setAnalysisResult] = useState(null);

  // Filtres
  const [measurementType, setMeasurementType] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return toLocalDatetimeString(d);
  });
  const [endDate, setEndDate] = useState(() => toLocalDatetimeString(new Date()));
  const [activeRange, setActiveRange] = useState(null); // label du bouton rapide actif

  // Progression de la récupération
  const [progression, setProgression] = useState(null); // null = pas en cours | { progression, recuperees, chunksCompleted, chunksTotal }

  // Ref pour l'AbortController (annulation)
  const abortControllerRef = useRef(null);

  // Statut du cache PostgreSQL
  const [cacheStatus, setCacheStatus] = useState(null); // { count, oldest, newest, types, sources, compartments }
  const [loadingCache, setLoadingCache] = useState(false);

  // Secteurs enrichis avec le nom du compartiment (pour le sélecteur de secteurs)
  const [sectors, setSectors] = useState([]);

  // -------------------------------------------------------------------------
  // Vérifier si l'utilisateur a configuré sa clé API
  // -------------------------------------------------------------------------
  const checkApiKey = useCallback(async () => {
    try {
      const response = await FarmRoadService.checkApiKeyStatus();
      setHasApiKey(response.hasKey);
      if (!response.hasKey) {
        setShowApiKeyModal(true);
      }
    } catch (err) {
      console.error('Erreur lors de la vérification de la clé API:', err);
      setHasApiKey(false);
      setShowApiKeyModal(true);
    }
  }, []);

  // Vérifier la clé API au montage du composant
  useEffect(() => {
    checkApiKey();
  }, [checkApiKey]);

  // -------------------------------------------------------------------------
  // Enregistrer la clé API de l'utilisateur
  // -------------------------------------------------------------------------
  const handleSaveApiKey = async (apiKey) => {
    setApiKeyLoading(true);
    setApiKeyError('');
    try {
      await FarmRoadService.configureApiKey(apiKey);
      setHasApiKey(true);
      setShowApiKeyModal(false);
      // Recharger les fermes après configuration de la clé
      loadFarms();
    } catch (err) {
      setApiKeyError(err.message || 'Clé API invalide');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Extraire les types de mesure uniques depuis les données
  // -------------------------------------------------------------------------
  const uniqueTypes = useMemo(() => {
    if (!measurements || measurements.length === 0) return [];
    const types = new Set();
    measurements.forEach(m => {
      if (m.type) types.add(m.type);
    });
    return Array.from(types).sort();
  }, [measurements]);

  // -------------------------------------------------------------------------
  // Chargement initial : liste des fermes
  // -------------------------------------------------------------------------
  const loadFarms = useCallback(async () => {
    setLoading(prev => ({ ...prev, farms: true }));
    setError(null);
    try {
      const data = await FarmRoadService.getFarms();
      const farmList = Array.isArray(data) ? data : (data.farms || []);
      setFarms(farmList);
      
      // Si une seule ferme est disponible, la sélectionner automatiquement
      if (farmList.length === 1 && !selectedFarm) {
        handleFarmSelect(farmList[0]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, farms: false }));
    }
  }, [selectedFarm]);

  useEffect(() => { loadFarms(); }, [loadFarms]);

  // -------------------------------------------------------------------------
  // Initialiser l'utilisateur connecté
  // -------------------------------------------------------------------------
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser?.uid) {
      setUserId(currentUser.uid);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Sélection d'une ferme
  // -------------------------------------------------------------------------
  const handleFarmSelect = async (farm) => {
    // Annuler une récupération en cours si elle existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setSelectedFarm(farm);
    setFarmDetail(null);
    setMeasurements([]);
    setProgression(null);
    setLoading(prev => ({ ...prev, detail: true }));
    setError(null);

    try {
      // OPTIMISATION : Charger depuis PostgreSQL au lieu de l'API FarmRoad
      // Récupérer les compartiments et secteurs depuis la base locale
      const [compartmentsData, sectorsData] = await Promise.all([
        FarmRoadService.getCompartments(farm.id),
        FarmRoadService.getSectors(farm.id)
      ]);

      // Reconstruire la structure de la ferme avec les données PostgreSQL
      const compartments = Array.isArray(compartmentsData) ? compartmentsData : [];
      const sectorsData_array = Array.isArray(sectorsData) ? sectorsData : [];

      // Enrichir les secteurs avec le nom du compartiment
      const enrichedSectors = sectorsData_array.map(sector => {
        const sectorCompartmentId = sector.compartmentId || sector.compartment_id;
        const compartment = compartments.find(c => (c.compartmentId || c.id) === sectorCompartmentId);
        const sectorIdValue = sector.sectorId || sector.sector_id || sector.id;
        return {
          ...sector,
          sectorId: sectorIdValue,  // Pour compatibilité avec certains composants
          sector_id: sectorIdValue,  // Pour FarmMapView et autres composants utilisant snake_case
          compartmentName: compartment?.name || null
        };
      });

      // Debug: Afficher les secteurs chargés
      console.log('🔍 [DEBUG] Secteurs chargés:', enrichedSectors.map(s => ({
        id: s.id,
        sectorId: s.sectorId,
        name: s.name,
        compartmentId: s.compartment_id || s.compartmentId
      })));

      // Organiser les secteurs par compartiment
      const compartmentsWithSectors = compartments.map(comp => ({
        ...comp,
        sectors: sectorsData_array.filter(sector => sector.compartment_id === comp.id)
      }));

      // Construire l'objet farmDetail compatible avec le format existant
      const detail = {
        id: farm.id,
        name: farm.name,
        location: farm.location,
        compartments: compartmentsWithSectors
      };

      setSectors(enrichedSectors);
      setFarmDetail(detail);
    } catch (err) {
      console.error('Erreur lors du chargement de la structure de la ferme:', err);
      // Si les données ne sont pas en cache, essayer de synchroniser
      try {
        console.log('Synchronisation de la structure de la ferme depuis FarmRoad...');
        await FarmRoadService.syncStructure(farm.id);

        // Réessayer de charger depuis PostgreSQL
        const [compartmentsData, sectorsData] = await Promise.all([
          FarmRoadService.getCompartments(farm.id),
          FarmRoadService.getSectors(farm.id)
        ]);

        const compartments = Array.isArray(compartmentsData) ? compartmentsData : [];
        const sectorsData_array = Array.isArray(sectorsData) ? sectorsData : [];

        // Enrichir les secteurs avec le nom du compartiment
        const enrichedSectors = sectorsData_array.map(sector => {
          const sectorCompartmentId = sector.compartmentId || sector.compartment_id;
          const compartment = compartments.find(c => (c.compartmentId || c.id) === sectorCompartmentId);
          const sectorIdValue = sector.sectorId || sector.sector_id || sector.id;
          return {
            ...sector,
            sectorId: sectorIdValue,  // Pour compatibilité avec certains composants
            sector_id: sectorIdValue,  // Pour FarmMapView et autres composants utilisant snake_case
            compartmentName: compartment?.name || null
          };
        });

        const compartmentsWithSectors = compartments.map(comp => ({
          ...comp,
          sectors: sectorsData_array.filter(sector => sector.compartment_id === comp.id)
        }));

        const detail = {
          id: farm.id,
          name: farm.name,
          location: farm.location,
          compartments: compartmentsWithSectors
        };

        setSectors(enrichedSectors);
        setFarmDetail(detail);
      } catch (syncErr) {
        console.error('Erreur lors de la synchronisation:', syncErr);
        setError(syncErr.message);
      }
    } finally {
      setLoading(prev => ({ ...prev, detail: false }));
    }
  };

  // -------------------------------------------------------------------------
  // Récupérer le statut du cache PostgreSQL
  // -------------------------------------------------------------------------
  const loadCacheStatus = useCallback(async () => {
    if (!selectedFarm) return;

    try {
      const status = await FarmRoadService.getCacheStatus(selectedFarm.id);
      setCacheStatus(status.cache || null);
    } catch (err) {
      console.warn('Erreur lors du chargement du statut du cache:', err.message);
      setCacheStatus(null);
    }
  }, [selectedFarm]);

  // Charger le statut du cache quand une ferme est sélectionnée
  useEffect(() => {
    if (selectedFarm) {
      loadCacheStatus();
    }
  }, [selectedFarm, loadCacheStatus]);

  // -------------------------------------------------------------------------
  // Mode 1 : Charger depuis le cache PostgreSQL (RAPIDE)
  // -------------------------------------------------------------------------
  const loadFromCache = useCallback(async () => {
    if (!selectedFarm) return;

    setLoadingCache(true);
    setMeasurements([]);
    setError(null);

    try {
      const params = {
        farmId:    selectedFarm.id,
        startTime: new Date(startDate).toISOString(),
        endTime:   new Date(endDate).toISOString()
      };
      if (measurementType) params.type = measurementType;

      const data = await FarmRoadService.getMeasurementsFromCache(params);
      setMeasurements(Array.isArray(data) ? data : []);

      // Rafraîchir le statut du cache après chargement
      await loadCacheStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCache(false);
    }
  }, [selectedFarm, startDate, endDate, measurementType, loadCacheStatus]);

  // -------------------------------------------------------------------------
  // Mode 2 : Rafraîchir depuis l'API FarmRoad (met à jour PostgreSQL)
  // -------------------------------------------------------------------------
  const refreshFromAPI = useCallback(async () => {
    if (!selectedFarm) return;

    // Annuler une récupération précédente si elle tourne encore
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(prev => ({ ...prev, measurements: true }));
    setMeasurements([]);
    setProgression({ progression: 0, recuperees: 0, chunksCompleted: 0, chunksTotal: 0 });
    setError(null);

    try {
      const params = {
        farmId:    selectedFarm.id,
        startTime: new Date(startDate).toISOString(),
        endTime:   new Date(endDate).toISOString()
      };
      if (measurementType) params.type = measurementType;

      // Utiliser la méthode parallèle qui appelle l'API et met à jour PostgreSQL
      const data = await FarmRoadService.getMeasurementsParallel(params, {
        batchSize: 10,
        signal: controller.signal,
        onProgress: (info) => {
          setProgression(info);
        }
      });

      if (!controller.signal.aborted) {
        setMeasurements(data);
        // Rafraîchir le statut du cache après synchronisation
        await loadCacheStatus();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setLoading(prev => ({ ...prev, measurements: false }));
      setProgression(null);
      abortControllerRef.current = null;
    }
  }, [selectedFarm, startDate, endDate, measurementType, loadCacheStatus]);

  // -------------------------------------------------------------------------
  // Boutons de plage rapide
  // -------------------------------------------------------------------------
  const handleQuickRange = (range) => {
    const { start, end } = range.compute();
    setStartDate(toLocalDatetimeString(start));
    setEndDate(toLocalDatetimeString(end));
    setActiveRange(range.label);
  };

  // Réinitialiser le bouton actif si l'utilisateur modifie manuellement les dates
  const handleManualDateChange = (setter) => (e) => {
    setter(e.target.value);
    setActiveRange(null);
  };


  // Nettoyage : annuler si le composant se démonte
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // -------------------------------------------------------------------------
  // Annuler la récupération en cours
  // -------------------------------------------------------------------------
  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  // -------------------------------------------------------------------------
  // Rendu
  // -------------------------------------------------------------------------
  const isRecuperating = loading.measurements && progression !== null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* ----------------------------------------------------------------- */}
        {/* Header                                                             */}
        {/* ----------------------------------------------------------------- */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Activity className="h-6 w-6 text-green-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Ferme Larache</h1>
              <p className="text-sm text-gray-500">
                <span className="hidden sm:inline">Exploitation agricole — </span>Données en temps réel
              </p>
            </div>
          </div>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw className="h-4 w-4" />}
            onClick={loadFarms}
            disabled={loading.farms}
            sx={{
              minWidth: { xs: 'auto', sm: 120 },
              px: { xs: 1.5, sm: 2 },
              '& .MuiButton-startIcon': {
                marginRight: { xs: 0, sm: 1 }
              }
            }}
            title="Recharger la liste des fermes depuis FarmRoad"
          >
            <span className="hidden sm:inline">Rafraîchir</span>
          </Button>
        </div>

        {/* ----------------------------------------------------------------- */}
        {/* Erreur globale                                                     */}
        {/* ----------------------------------------------------------------- */}
        {error && (
          <Alert severity="error" icon={<AlertCircle className="h-5 w-5" />}>
            {error}
          </Alert>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* Layout simplifié - Centré sur une colonne                         */}
        {/* ----------------------------------------------------------------- */}
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Sélecteur de ferme - affiché uniquement si plus d'une ferme */}
          {!loading.farms && farms.length > 1 && (
            <FarmSelector
              farms={farms}
              selectedFarm={selectedFarm}
              onChange={handleFarmSelect}
              loading={loading.detail}
            />
          )}



          {/* Message de chargement */}
          {loading.farms && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
              <CircularProgress size={28} />
              <p className="text-gray-500 mt-4">Chargement des fermes...</p>
            </div>
          )}

          {/* Aucune ferme */}
          {!loading.farms && farms.length === 0 && !error && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
              <Server className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune ferme trouvée. Vérifiez votre API Key.</p>
            </div>
          )}

          {/* Pas de ferme sélectionnée - seulement si plus d'une ferme */}
          {!selectedFarm && !loading.farms && farms.length > 1 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
              <Activity className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Sélectionnez une ferme dans la liste ci-dessus pour voir ses données.</p>
            </div>
          )}

          {/* --- Onglets : Mesures / Analyse IA ----------------------------- */}
          {selectedFarm && (
            <div className="flex flex-col md:flex-row gap-4">
              {/* Panneau principal (dashboard) */}
              <div className={`bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-300 ${showMap ? 'md:w-3/5 w-full' : 'w-full'}`}>
                {/* Onglets de navigation + bouton Carte */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: { xs: 1, sm: 2 }, py: { xs: 1, sm: 0 } }}>
                  {/* Sur mobile : Bouton Menu hamburger */}
                  {isMobile ? (
                    <Box
                      onClick={() => setDrawerOpen(true)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexGrow: 1,
                        cursor: 'pointer',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        },
                        '&:active': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      <Menu className="h-6 w-6" style={{ marginRight: 12, color: '#1976d2' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {['Météo', 'Mesures', 'Analyse IA', 'Historique', 'Paramètres', 'Automatisation'][currentTab]}
                      </Typography>
                    </Box>
                  ) : (
                    /* Sur desktop/tablette : Tabs scrollables */
                    <Tabs
                      value={currentTab}
                      onChange={(e, newValue) => setCurrentTab(newValue)}
                      aria-label="Onglets tableau de bord"
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      sx={{
                        flexGrow: 1,
                        '& .MuiTab-root': {
                          minWidth: { xs: 'auto', sm: 120 },
                          px: { xs: 1, sm: 2 },
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                    >
                      <Tab
                        icon={<Cloud className="h-4 w-4" />}
                        label="Météo"
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 500 }}
                      />
                      <Tab
                        icon={<BarChart3 className="h-4 w-4" />}
                        label="Mesures"
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 500 }}
                      />
                      <Tab
                        icon={<Sparkles className="h-4 w-4" />}
                        label="Analyse IA"
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 500 }}
                      />
                      <Tab
                        icon={<History className="h-4 w-4" />}
                        label="Historique"
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 500 }}
                      />
                      <Tab
                        icon={<Settings className="h-4 w-4" />}
                        label="Paramètres"
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 500 }}
                      />
                      <Tab
                        icon={<Zap className="h-4 w-4" />}
                        label="Automatisation"
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontWeight: 500 }}
                      />
                    </Tabs>
                  )}

                  {/* Bouton toggle carte */}
                  <Button
                    variant={showMap ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setShowMap(!showMap)}
                    startIcon={<Map className="h-4 w-4" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      minWidth: { xs: 'auto', sm: 140 },
                      px: { xs: 1.5, sm: 2 },
                      '& .MuiButton-startIcon': {
                        marginRight: { xs: 0, sm: 1 }
                      }
                    }}
                  >
                    <span className="hidden sm:inline">{showMap ? 'Masquer carte' : 'Afficher carte'}</span>
                  </Button>
                </Box>

                {/* Drawer menu pour mobile */}
                <Drawer
                  anchor="left"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{
                    sx: {
                      width: 280,
                      bgcolor: 'background.paper'
                    }
                  }}
                >
                  {/* Header du drawer */}
                  <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Navigation
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {selectedFarm?.name || 'Tableau de bord'}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Liste des options de navigation */}
                  <List sx={{ pt: 1 }}>
                    {[
                      { index: 0, icon: <Cloud className="h-5 w-5" />, label: 'Météo', color: '#3b82f6' },
                      { index: 1, icon: <BarChart3 className="h-5 w-5" />, label: 'Mesures', color: '#10b981' },
                      { index: 2, icon: <Sparkles className="h-5 w-5" />, label: 'Analyse IA', color: '#8b5cf6' },
                      { index: 3, icon: <History className="h-5 w-5" />, label: 'Historique', color: '#f59e0b' },
                      { index: 4, icon: <Settings className="h-5 w-5" />, label: 'Paramètres', color: '#6b7280' },
                      { index: 5, icon: <Zap className="h-5 w-5" />, label: 'Automatisation', color: '#ef4444' }
                    ].map((item) => (
                      <ListItemButton
                        key={item.index}
                        selected={currentTab === item.index}
                        onClick={() => {
                          setCurrentTab(item.index);
                          setDrawerOpen(false);
                        }}
                        sx={{
                          mx: 1,
                          mb: 0.5,
                          borderRadius: 2,
                          '&.Mui-selected': {
                            bgcolor: 'primary.light',
                            color: 'primary.main',
                            fontWeight: 600,
                            '&:hover': {
                              bgcolor: 'primary.light'
                            }
                          },
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ color: currentTab === item.index ? item.color : 'text.secondary', minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontWeight: currentTab === item.index ? 600 : 400,
                            fontSize: '0.95rem'
                          }}
                        />
                        {currentTab === item.index && (
                          <ChevronRight className="h-4 w-4" style={{ color: item.color }} />
                        )}
                      </ListItemButton>
                    ))}
                  </List>

                  <Divider sx={{ mt: 'auto' }} />

                  {/* Footer du drawer (optionnel) */}
                  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">
                      FarmRoad Dashboard v2.0
                    </Typography>
                  </Box>
                </Drawer>

                {/* Contenu de l'onglet Météo */}
                {currentTab === 0 && (
                  <div className="p-6">
                    <FarmRoadWeatherPanel
                      farmId={selectedFarm?.id}
                      farmName={selectedFarm?.name}
                      farmDetail={farmDetail}
                    />
                  </div>
                )}

                {/* Contenu de l'onglet Mesures */}
                {currentTab === 1 && (
                  <div>

                {/* Filtres */}
                <div className="p-3 sm:p-4 bg-gray-50 border-b border-gray-100 space-y-3">

                  {/* Boutons plages rapides */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">Plage rapide</label>
                    <div className="flex flex-wrap gap-2">
                      {QUICK_RANGES.map((range) => (
                        <button
                          key={range.label}
                          onClick={() => handleQuickRange(range)}
                          className={[
                            'px-3 py-2 sm:py-1 rounded-full text-sm sm:text-xs font-medium border transition-colors',
                            activeRange === range.label
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 active:bg-gray-50'
                          ].join(' ')}
                        >
                          {range.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Grille : type + dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Type de mesure */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Type de mesure</label>
                      <select
                        value={measurementType}
                        onChange={(e) => setMeasurementType(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:border-blue-400 focus:outline-none"
                      >
                        <option value="">Tous les types</option>
                        {uniqueTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    {/* Début */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        <Calendar className="h-3 w-3 inline mr-1" />Début
                      </label>
                      <input
                        type="datetime-local"
                        value={startDate}
                        onChange={handleManualDateChange(setStartDate)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                      />
                    </div>

                    {/* Fin */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        <Calendar className="h-3 w-3 inline mr-1" />Fin
                      </label>
                      <input
                        type="datetime-local"
                        value={endDate}
                        onChange={handleManualDateChange(setEndDate)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Statut du cache PostgreSQL */}
                  {cacheStatus && cacheStatus.totalMeasurements > 0 && (
                    <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800">
                      <strong>Cache PostgreSQL :</strong> {cacheStatus.totalMeasurements.toLocaleString('fr-FR')} mesures stockées
                      {cacheStatus.oldestMeasurement && cacheStatus.newestMeasurement && (
                        <span className="ml-2">
                          (du {new Date(cacheStatus.oldestMeasurement).toLocaleDateString('fr-FR')} au {new Date(cacheStatus.newestMeasurement).toLocaleDateString('fr-FR')})
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions : Charger + Rafraîchir + Annuler + Export */}
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    {/* Bouton 1 : Charger depuis le cache (rapide) */}
                    <Button
                      variant="contained"
                      size={isMobile ? "medium" : "small"}
                      color="primary"
                      onClick={loadFromCache}
                      disabled={loadingCache || isRecuperating}
                      startIcon={loadingCache ? <CircularProgress size={16} color="inherit" /> : <Activity className="h-4 w-4" />}
                      sx={{
                        minWidth: { xs: 'auto', sm: 140 },
                        px: { xs: 2, sm: 2 }
                      }}
                    >
                      {loadingCache ? 'Chargement...' : (isMobile ? 'Charger' : 'Charger les données')}
                    </Button>

                    {/* Bouton 2 : Rafraîchir depuis FarmRoad API (lent mais à jour) */}
                    <Button
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      color="success"
                      onClick={refreshFromAPI}
                      disabled={loadingCache || isRecuperating}
                      startIcon={isRecuperating ? <CircularProgress size={16} color="inherit" /> : <RefreshCw className="h-4 w-4" />}
                      sx={{
                        minWidth: { xs: 'auto', sm: 140 },
                        px: { xs: 2, sm: 2 }
                      }}
                    >
                      {isRecuperating ? (isMobile ? 'Rafraîchit...' : 'Rafraîchissement...') : (isMobile ? 'Rafraîchir' : 'Rafraîchir depuis FarmRoad')}
                    </Button>

                    {/* Annuler (pendant rafraîchissement API) */}
                    {isRecuperating && (
                      <Button
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        color="error"
                        onClick={handleCancel}
                        startIcon={<X className="h-4 w-4" />}
                        sx={{
                          minWidth: { xs: 'auto', sm: 100 },
                          px: { xs: 2, sm: 2 }
                        }}
                      >
                        Annuler
                      </Button>
                    )}

                    {/* Export CSV */}
                    {measurements.length > 0 && !isRecuperating && !loadingCache && (
                      <Button
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        onClick={() => exportCSV(measurements, selectedFarm?.id, farmDetail)}
                        startIcon={<Download className="h-4 w-4" />}
                        sx={{
                          minWidth: { xs: 'auto', sm: 120 },
                          px: { xs: 2, sm: 2 }
                        }}
                      >
                        {isMobile ? `CSV (${measurements.length})` : `Export CSV (${measurements.length})`}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Barre de progression */}
                {progression && (
                  <div className="px-4 pt-3 pb-1 bg-gray-50 border-b border-gray-100">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>
                        Lot {progression.chunksCompleted} / {progression.chunksTotal}
                        {progression.recuperees > 0 && ` — ${progression.recuperees.toLocaleString('fr-FR')} mesures récupérées`}
                      </span>
                      <span>{Math.round(progression.progression * 100)} %</span>
                    </div>
                    <LinearProgress variant="determinate" value={progression.progression * 100} color="success" />
                  </div>
                )}

                {/* Table des mesures */}
                <div className="p-4">
                  {(loading.measurements || loadingCache) && measurements.length === 0 && (
                    <div className="flex justify-center py-8"><CircularProgress size={28} /></div>
                  )}
                  {!loading.measurements && !loadingCache && (
                    <MeasurementsTable
                      measurements={measurements}
                      filterType={measurementType}
                      farmDetail={farmDetail}
                      farmId={selectedFarm?.id}
                    />
                  )}
                </div>
                  </div>
                )}

                {/* Contenu de l'onglet Analyse IA */}
                {currentTab === 2 && (
                  <div>
                    <FarmRoadInsightsPanel
                      userId={userId}
                      farmId={selectedFarm?.id}
                      farmName={selectedFarm?.name}
                      farmDetail={farmDetail}
                      sectors={sectors}
                      culture={farmDetail?.culture || 'generic'}
                      objective={farmDetail?.objective || 'Optimisation de la culture sous serre'}
                      analysisResult={analysisResult}
                      onAnalysisComplete={setAnalysisResult}
                    />
                  </div>
                )}

                {/* Contenu de l'onglet Historique */}
                {currentTab === 3 && (
                  <div className="p-6">
                    <FarmRoadHistoryPanel
                      farmId={selectedFarm?.id}
                      farmName={selectedFarm?.name}
                      sectors={sectors}
                    />
                  </div>
                )}

                {/* Contenu de l'onglet Paramètres */}
                {currentTab === 4 && (
                  <div>
                    <FarmRoadSettingsPanel
                      farmId={selectedFarm?.id}
                      farmName={selectedFarm?.name}
                    />
                  </div>
                )}

                {/* Contenu de l'onglet Automatisation */}
                {currentTab === 5 && (
                  <div>
                    <FarmRoadAutomationPanel
                      farmId={selectedFarm?.id}
                      farmName={selectedFarm?.name}
                    />
                  </div>
                )}
              </div>

              {/* Panneau carte (à droite) */}
              {showMap && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm md:w-2/5 w-full order-first md:order-last transition-all duration-300">
                  <FarmMapView
                    farmId={selectedFarm?.id}
                    farmDetail={farmDetail}
                    sectors={sectors}
                    measurements={measurements}
                    onSensorSelect={setSelectedSensor}
                    onSectorSelect={setSelectedSectorId}
                  />
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Modal de configuration de clé API */}
      <ApiKeyConfigModal
        open={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        onSave={handleSaveApiKey}
        loading={apiKeyLoading}
        error={apiKeyError}
      />
    </div>
  );
};

export default FarmRoadDashboard;
